import { Drawer } from 'antd';
import styled from 'styled-components';

import type { Layouts } from './utils';

import * as Cover from './cover';
import * as ImageHalf from './image-half';
import * as Text from './text';
import * as Buttons from './buttons';
import * as Collage from './collage';
import * as Posts from './posts';
import * as Signup from './signup';
import * as HeaderSpacer from './header-spacer';
import * as Counter from './counter';
import * as XmasPosts from './xmas-posts';

export const layouts: Layouts = {
	Cover: {
		panel: Cover.coverLayoutPanels,
		Component: Cover.CoverLayout,
		values: Cover.coverLayoutValues,
	},

	ImageHalf: {
		panel: ImageHalf.imageHalfLayoutPanels,
		Component: ImageHalf.ImageHalfLayout,
		values: ImageHalf.imageHalfLayoutValues,
	},

	Text: {
		panel: Text.textLayoutPanels,
		Component: Text.TextLayout,
		values: Text.textLayoutValues,
	},

	Buttons: {
		panel: Buttons.buttonLayoutPanels,
		Component: Buttons.ButtonLayout,
		values: Buttons.buttonLayoutValues,
	},

	Collage: {
		panel: Collage.collageLayoutPanels,
		Component: Collage.CollageLayout,
		values: Collage.collageLayoutValues,
	},

	Posts: {
		panel: Posts.postsLayoutPanels,
		Component: Posts.PostsLayout,
		values: Posts.postsLayoutValues,
	},

	Signup: {
		panel: Signup.signupLayoutPanels,
		Component: Signup.SignupLayout,
		values: Signup.signupLayoutValues,
	},

	HeaderSpacer: {
		panel: HeaderSpacer.headerSpacerLayoutPanels,
		Component: HeaderSpacer.HeaderSpacerLayout,
		values: HeaderSpacer.headerSpacerLayoutValues,
	},

	Counter: {
		panel: Counter.counterLayoutPanels,
		Component: Counter.CounterLayout,
		values: Counter.counterLayoutValues,
	},

	XmasPosts: {
		panel: XmasPosts.xmasPostsLayoutPanels,
		Component: XmasPosts.XmasPostsLayout,
		values: XmasPosts.XmasPostsLayoutValues,
	},
};

export const getLayoutPanel = (layoutName: string) => {
	if (layoutName === null) return null;

	const layout = layouts[layoutName] ?? null;
	if (layout === null) return null;

	return layout?.panel ?? null;
};

export const getLayout = (layoutName: string) => {
	if (layoutName === null) return null;
	const layout = layouts[layoutName] ?? null;
	return layout ?? null;
};

export const sectionRenderer = (layoutName: string, section: any) => {
	const layout = layouts[layoutName] ?? null;
	if (layout === null) return null;

	return <layout.Component {...section} />;
};

export type LayoutDrawerProps = {
	visible: boolean;
	onClose: () => void;
	onSelect: (layoutName: string, layout: Layouts[string]) => void;
};

export const LayoutDrawer = (props: LayoutDrawerProps) => {
	return (
		<LayoutDrawerStyled
			width={420}
			title="Layouts"
			placement="left"
			visible={props.visible}
			onClose={props.onClose}
		>
			{Object.entries(layouts).map(([layoutName, layout]) => (
				<section
					key={layoutName}
					className={'layout-container ' + layoutName}
					onClick={() => props.onSelect(layoutName, layout)}
				>
					{sectionRenderer(layoutName, layout.values)}
				</section>
			))}
		</LayoutDrawerStyled>
	);
};

const LayoutDrawerStyled = styled(Drawer)`
	.ant-drawer-body {
		display: flex;
		flex-direction: column;
		gap: 1ch;
	}

	.layout-container {
		zoom: 0.5;
		cursor: pointer;

		border-radius: 1rem;
		box-shadow: 0 0 1rem #0001;

		-webkit-user-select: none; /* Safari */
		-ms-user-select: none; /* IE 10 and IE 11 */
		user-select: none; /* Standard syntax */

		& > div {
			border-radius: 1ch;
			overflow: hidden;

			&:hover {
				box-shadow: 0 0 2ch #aaa;
			}
		}
	}

	h1,
	h2,
	h3,
	p {
		font-size: 1rem;
		margin: 0;
		padding: 0;
	}

	.Signup {
		.text-container {
			zoom: 0.5;
		}

		.form-container {
			zoom: 0.5;
		}
	}

	.XmasPosts {
		zoom: 0.3;
	}
`;
